import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Stack,
  Chip,
  Button,
  Card,
  alpha,
} from '@mui/material';
import {
  ExpandMore,
  Search as SearchIcon,
  RocketLaunch,
  VideoCall,
  People,
  Event,
  Security,
  Build,
  Help as HelpIcon,
  ArrowBack,
  RecordVoiceOver,
  Assignment,
} from '@mui/icons-material';

const FAQ_CATEGORIES = {
  ALL: 'all',
  GETTING_STARTED: 'getting-started',
  ACCOUNT: 'account',
  PAIRING: 'pairing',
  MEETINGS: 'meetings',
  VIDEO_CALLS: 'video-calls',
  AI_NOTETAKER: 'ai-notetaker',
  GOALS_SURVEYS: 'goals-surveys',
  TROUBLESHOOTING: 'troubleshooting',
};

const FAQS = [
  // GETTING STARTED
  {
    id: 1,
    category: FAQ_CATEGORIES.GETTING_STARTED,
    question: 'What is Edifying Teachers?',
    answer: 'Edifying Teachers is a mentorship platform that connects teachers with experienced educational professionals (Edifiers) to support professional growth and development. The platform facilitates meaningful mentorship relationships through video meetings, goal setting, and reflective surveys.',
  },
  {
    id: 2,
    category: FAQ_CATEGORIES.GETTING_STARTED,
    question: 'How do I get started on the platform?',
    answer: 'After registering, complete your profile with a photo and professional information. Set your availability schedule so others can see when you\'re free for meetings. If you\'re a teacher, browse available Edifiers and request a pairing. If you\'re an Edifier, wait for pairing assignments from administrators. Once paired, you can start scheduling meetings.',
  },
  {
    id: 3,
    category: FAQ_CATEGORIES.GETTING_STARTED,
    question: 'What is the difference between a Teacher and an Edifier?',
    answer: 'Teachers are educators seeking mentorship and professional development support. Edifiers (also called Coaches) are experienced educational professionals who serve as mentors, providing guidance, support, and encouragement to teachers. Edifiers help teachers develop skills, overcome challenges, and achieve professional goals.',
  },

  // ACCOUNT
  {
    id: 4,
    category: FAQ_CATEGORIES.ACCOUNT,
    question: 'How do I reset my password?',
    answer: 'Click "Forgot Password?" on the login page, enter your registered email address, and you\'ll receive a password reset link. Follow the link in the email to create a new password. The reset link expires after 24 hours.',
  },
  {
    id: 5,
    category: FAQ_CATEGORIES.ACCOUNT,
    question: 'Why do I need to verify my email?',
    answer: 'Email verification ensures you own the email address, enables password recovery, ensures you receive important notifications, and helps maintain platform security. You cannot log in until your email is verified.',
  },
  {
    id: 6,
    category: FAQ_CATEGORIES.ACCOUNT,
    question: 'Can I change my profile picture or introduction video?',
    answer: 'Yes! Go to "My Profile" and click the camera icon on your profile picture to upload a new photo. For Edifiers, you can upload or change your introduction video in the profile section. Videos help teachers understand your mentoring style.',
  },
  {
    id: 7,
    category: FAQ_CATEGORIES.ACCOUNT,
    question: 'What is DocuSign and why do I need to complete it?',
    answer: 'DocuSign is an electronic signature system for signing required agreements and consent forms. You must complete DocuSign verification before accessing most platform features. You\'ll receive a signing URL after registration, and the platform will remind you to complete it if pending.',
  },

  // PAIRING & MENTORSHIP
  {
    id: 8,
    category: FAQ_CATEGORIES.PAIRING,
    question: 'How do I get paired with an Edifier?',
    answer: 'Teachers can browse available Edifiers in "Find Edifiers" and view their profiles, introduction videos, and specialties. When you find a good match, click "Request Pairing." An administrator will review your request and either approve it or suggest an alternative. You\'ll be notified when your pairing is active.',
  },
  {
    id: 9,
    category: FAQ_CATEGORIES.PAIRING,
    question: 'Can I have more than one Edifier?',
    answer: 'Yes! Teachers can be paired with multiple Edifiers for different areas of support. For example, you might have one Edifier for classroom management and another for technology integration. Each pairing is independent with its own meeting schedule and goals.',
  },
  {
    id: 10,
    category: FAQ_CATEGORIES.PAIRING,
    question: 'What if my pairing isn\'t working out?',
    answer: 'First, try communicating your concerns openly with your Edifier. If issues persist, contact your site leader or administrator to discuss the situation. They can facilitate a conversation, provide additional support, or arrange a new pairing if necessary. Finding the right fit is important for successful mentorship.',
  },
  {
    id: 11,
    category: FAQ_CATEGORIES.PAIRING,
    question: 'How often should I meet with my Edifier/Teacher?',
    answer: 'Most pairings meet weekly or bi-weekly for 30-60 minutes per session. Consistency is key to building momentum and trust. You and your partner should agree on a regular schedule that works for both of you. The platform helps track your meeting frequency.',
  },

  // MEETINGS & SCHEDULING
  {
    id: 12,
    category: FAQ_CATEGORIES.MEETINGS,
    question: 'How do I schedule a meeting?',
    answer: 'Go to "Your Edifiers" or "Your Teachers," click on the person you want to meet with, and select "Schedule Meeting." You\'ll see their available time slots. Click on a time to send an invite. They\'ll receive a notification and can accept or decline. Once accepted, the meeting appears in "Upcoming Events" for both participants.',
  },
  {
    id: 13,
    category: FAQ_CATEGORIES.MEETINGS,
    question: 'How do I set my availability?',
    answer: 'Navigate to "My Availability" from the dashboard. Click on time slots to mark them as available (or click again to remove availability). Select all the times you\'re typically free for meetings. Your availability repeats weekly, but you can block out specific dates for vacations or busy periods. Remember to save your changes!',
  },
  {
    id: 14,
    category: FAQ_CATEGORIES.MEETINGS,
    question: 'Can I cancel or reschedule a meeting?',
    answer: 'Yes. To cancel, go to "Upcoming Events," find the meeting, and click "Cancel Meeting." Provide a reason if possible. To reschedule, cancel the current meeting and send a new invite for a different time. Try to give at least 24 hours notice when canceling or rescheduling.',
  },
  {
    id: 15,
    category: FAQ_CATEGORIES.MEETINGS,
    question: 'What are meeting reminders?',
    answer: 'The platform sends automatic reminders 24 hours before your meeting, 1 hour before, and 10 minutes before (with a join link). Reminders are sent via email and in-app notifications to help ensure you don\'t miss your meetings.',
  },

  // VIDEO CALLS
  {
    id: 16,
    category: FAQ_CATEGORIES.VIDEO_CALLS,
    question: 'How do I join a video call?',
    answer: 'Go to "Upcoming Events" and find your meeting. The "Join Meeting" button appears 10 minutes before the scheduled start time. Click it, allow camera and microphone permissions when prompted, preview your video, then click "Join Now" to enter the meeting room.',
  },
  {
    id: 17,
    category: FAQ_CATEGORIES.VIDEO_CALLS,
    question: 'What if my camera or microphone isn\'t working?',
    answer: 'First, check browser permissions - click the lock icon in your address bar and ensure Camera and Microphone are set to "Allow." Make sure no other applications are using your camera. Try closing other video apps, refreshing your browser, or restarting your computer. Chrome is the recommended browser for best performance.',
  },
  {
    id: 18,
    category: FAQ_CATEGORIES.VIDEO_CALLS,
    question: 'Can I share my screen during a meeting?',
    answer: 'Yes! Click the "Share Screen" button during your video call, select which window or screen you want to share, and click "Share." The other participant will see your screen. Click "Stop Sharing" when done. Screen sharing is useful for demonstrating lesson plans, showing student work, or reviewing documents together.',
  },
  {
    id: 19,
    category: FAQ_CATEGORIES.VIDEO_CALLS,
    question: 'What should I do if the video call is lagging or freezing?',
    answer: 'Try these quick fixes: turn off your video temporarily, close other applications, move closer to your WiFi router, or use a wired ethernet connection. For persistent issues, check your internet speed (need 1.5+ Mbps), close unnecessary browser tabs, or try a different browser.',
  },
  {
    id: 20,
    category: FAQ_CATEGORIES.VIDEO_CALLS,
    question: 'Which browsers work best for video calls?',
    answer: 'Google Chrome (latest version) works best. Microsoft Edge (Chromium) and Firefox (latest) also work well. Safari and Internet Explorer are not recommended. Make sure your browser is updated, JavaScript is enabled, and cookies are allowed.',
  },

  // AI NOTE-TAKER
  {
    id: 21,
    category: FAQ_CATEGORIES.AI_NOTETAKER,
    question: 'What is the AI Note-Taker?',
    answer: 'The AI Note-Taker automatically transcribes your video call conversations and generates an intelligent summary after the meeting. It captures key discussion points, action items, highlights, and next steps, then emails the summary to both participants so you can focus on the conversation instead of taking notes.',
  },
  {
    id: 22,
    category: FAQ_CATEGORIES.AI_NOTETAKER,
    question: 'Who decides if the AI Note-Taker is used?',
    answer: 'Only the Edifier sees a prompt at the start of each meeting asking to allow or deny the AI Note-Taker. Teachers do not see this prompt - the Edifier\'s decision applies to the entire meeting. This gives mentors control over recording while maintaining a simple experience for teachers.',
  },
  {
    id: 23,
    category: FAQ_CATEGORIES.AI_NOTETAKER,
    question: 'Is my meeting conversation private and secure?',
    answer: 'Yes. Transcripts are encrypted and stored securely. Only the two meeting participants can access their meeting transcript and AI summary - administrators cannot view them. Transcripts are retained for one year, then automatically deleted. All data handling complies with privacy regulations.',
  },
  {
    id: 24,
    category: FAQ_CATEGORIES.AI_NOTETAKER,
    question: 'What if the AI Note-Taker is denied?',
    answer: 'If the Edifier declines the AI Note-Taker, only a basic transcript is recorded for record-keeping purposes. No AI summary is generated, and no emails are sent. You\'ll need to take your own notes during the meeting.',
  },

  // GOALS & SURVEYS
  {
    id: 25,
    category: FAQ_CATEGORIES.GOALS_SURVEYS,
    question: 'What are "Ms & Cs" surveys?',
    answer: '"Ms & Cs" stands for "Moments and Celebrations." After each meeting, participants complete a brief survey to reflect on key insights (Moments) and positive developments (Celebrations). This reflection helps track progress, maintain accountability, and celebrate growth over time.',
  },
  {
    id: 26,
    category: FAQ_CATEGORIES.GOALS_SURVEYS,
    question: 'How do goals work?',
    answer: 'Edifiers set professional development goals for their paired teachers during or after meetings. Goals appear on the teacher\'s dashboard and can be reviewed in future meetings. Teachers mark goals as complete when achieved. This creates a clear roadmap for professional growth with visible progress tracking.',
  },
  {
    id: 27,
    category: FAQ_CATEGORIES.GOALS_SURVEYS,
    question: 'Do I have to complete the post-meeting survey?',
    answer: 'While not technically required, completing the survey is highly encouraged. It helps you reflect on your learning, documents your professional growth, creates accountability, and provides valuable data for program improvement. The survey only takes 2-3 minutes and appears right after your meeting ends.',
  },
  {
    id: 28,
    category: FAQ_CATEGORIES.GOALS_SURVEYS,
    question: 'Can I see my survey history?',
    answer: 'Yes! Your past surveys and "Ms & Cs" appear on your dashboard. Review them anytime to see your journey, track themes and patterns, prepare for evaluations, or celebrate how far you\'ve come. Your survey responses are private between you and your paired partner.',
  },

  // TROUBLESHOOTING
  {
    id: 29,
    category: FAQ_CATEGORIES.TROUBLESHOOTING,
    question: 'Why can\'t I log in?',
    answer: 'Common reasons: incorrect email or password (check for typos, Caps Lock), account not verified (check email for verification link), account deactivated, or too many failed login attempts (wait 15-30 minutes). If none of these apply, use "Forgot Password" to reset your password or contact support.',
  },
  {
    id: 30,
    category: FAQ_CATEGORIES.TROUBLESHOOTING,
    question: 'I\'m not receiving email notifications. What should I do?',
    answer: 'Check your spam/junk folder first. Add the platform email address to your contacts. Verify your email address is correct in your profile. Check if emails are being filtered by your IT department. Ensure notification preferences are enabled in your settings. If issues persist after 30 minutes, contact support.',
  },
  {
    id: 31,
    category: FAQ_CATEGORIES.TROUBLESHOOTING,
    question: 'The "Join Meeting" button isn\'t showing. Why?',
    answer: 'The button only appears 10 minutes before the meeting start time. Check if you\'re viewing the correct time zone. Verify the meeting wasn\'t cancelled. Try refreshing the page. If the meeting should have started and the button still doesn\'t appear, contact support immediately.',
  },
  {
    id: 32,
    category: FAQ_CATEGORIES.TROUBLESHOOTING,
    question: 'How do I contact support?',
    answer: 'Click the Help icon (?) in the dashboard header and select "Contact Support," or email support@edifyingteachers.com directly. Include your name, email, what you were trying to do, what happened, and any error messages. Screenshots are helpful! Support typically responds within 24 hours on business days.',
  },
  {
    id: 33,
    category: FAQ_CATEGORIES.TROUBLESHOOTING,
    question: 'Can I use the platform on my phone or tablet?',
    answer: 'Yes, the platform is mobile-responsive and works on phones and tablets. However, video calls work best on desktop computers with a stable internet connection. Mobile browsers (Safari, Chrome mobile) are supported. For the best experience, use landscape orientation on tablets during video calls.',
  },

  // ADDITIONAL PLATFORM-SPECIFIC
  {
    id: 34,
    category: FAQ_CATEGORIES.GETTING_STARTED,
    question: 'What are the different user roles?',
    answer: 'Admin - Full platform access, manages users and pairings. Site Leader - Manages users and pairings for their school/district. Principal - Similar to Site Leader, school-specific oversight. Edifier/Coach - Experienced educators who mentor teachers. Teacher - Educators seeking mentorship and support.',
  },
  {
    id: 35,
    category: FAQ_CATEGORIES.MEETINGS,
    question: 'What is check-in and check-out?',
    answer: 'Check-in records when you join a meeting, and check-out records when you leave. This tracks session duration and attendance for reporting purposes. Check-in/out happens automatically when you join and leave video calls, but may also be used for in-person meetings.',
  },
  {
    id: 36,
    category: FAQ_CATEGORIES.ACCOUNT,
    question: 'Can I change my role on the platform?',
    answer: 'Role changes must be made by an administrator. If you believe your role is incorrect or needs to be updated, contact your site leader or platform administrator. They can update your credentials and role through the "Manage Credentials" section.',
  },
];

const CATEGORY_INFO = [
  {
    id: FAQ_CATEGORIES.GETTING_STARTED,
    name: 'Getting Started',
    icon: RocketLaunch,
    color: '#A94B9C',
  },
  {
    id: FAQ_CATEGORIES.ACCOUNT,
    name: 'Account & Profile',
    icon: People,
    color: '#4B7BA9',
  },
  {
    id: FAQ_CATEGORIES.PAIRING,
    name: 'Pairing & Mentorship',
    icon: People,
    color: '#A97B4B',
  },
  {
    id: FAQ_CATEGORIES.MEETINGS,
    name: 'Meetings & Scheduling',
    icon: Event,
    color: '#7BA94B',
  },
  {
    id: FAQ_CATEGORIES.VIDEO_CALLS,
    name: 'Video Calls',
    icon: VideoCall,
    color: '#9C4BA9',
  },
  {
    id: FAQ_CATEGORIES.AI_NOTETAKER,
    name: 'AI Note-Taker',
    icon: RecordVoiceOver,
    color: '#4BA99C',
  },
  {
    id: FAQ_CATEGORIES.GOALS_SURVEYS,
    name: 'Goals & Surveys',
    icon: Assignment,
    color: '#A94B7B',
  },
  {
    id: FAQ_CATEGORIES.TROUBLESHOOTING,
    name: 'Troubleshooting',
    icon: Build,
    color: '#666666',
  },
];

function FAQs() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(FAQ_CATEGORIES.ALL);
  const [expanded, setExpanded] = useState(false);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Filter FAQs
  const filteredFAQs = FAQS.filter((faq) => {
    const matchesCategory = selectedCategory === FAQ_CATEGORIES.ALL || faq.category === selectedCategory;
    const matchesSearch =
      searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryInfo = (categoryId) => {
    return CATEGORY_INFO.find((cat) => cat.id === categoryId);
  };

  return (
    <>
      <Helmet>
        <title>FAQs | Edifying Teachers</title>
        <meta name="description" content="Frequently asked questions about Edifying Teachers platform" />
      </Helmet>

      <Container
        maxWidth={false}
        sx={{
          py: { xs: 2, sm: 3, md: 4 },
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/help')}
            sx={{
              mb: 2,
              color: '#A94B9C',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: alpha('#A94B9C', 0.1),
              },
            }}
          >
            Back to Help Center
          </Button>

          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              color: '#A94B9C',
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 800 }}>
            Quick answers to common questions about using Edifying Teachers platform
          </Typography>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            placeholder="Search FAQs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#A94B9C' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: 600,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'white',
                boxShadow: '0 2px 10px rgba(169, 75, 156, 0.1)',
              },
            }}
          />
        </Box>

        {/* Category Filter */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Filter by Category:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Chip
              label="All"
              onClick={() => setSelectedCategory(FAQ_CATEGORIES.ALL)}
              sx={{
                fontWeight: 600,
                cursor: 'pointer',
                ...(selectedCategory === FAQ_CATEGORIES.ALL && {
                  backgroundColor: '#A94B9C',
                  color: 'white',
                }),
              }}
            />
            {CATEGORY_INFO.map((category) => {
              const IconComponent = category.icon;
              return (
                <Chip
                  key={category.id}
                  icon={<IconComponent sx={{ fontSize: 18 }} />}
                  label={category.name}
                  onClick={() => setSelectedCategory(category.id)}
                  sx={{
                    fontWeight: 600,
                    cursor: 'pointer',
                    ...(selectedCategory === category.id && {
                      backgroundColor: category.color,
                      color: 'white',
                      '& .MuiChip-icon': {
                        color: 'white',
                      },
                    }),
                  }}
                />
              );
            })}
          </Stack>
        </Box>

        {/* Results Count */}
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Showing {filteredFAQs.length} question{filteredFAQs.length !== 1 ? 's' : ''}
        </Typography>

        {/* FAQ Accordions */}
        <Box>
          {filteredFAQs.length === 0 ? (
            <Card sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
              <HelpIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No FAQs found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting your search or selecting a different category
              </Typography>
            </Card>
          ) : (
            filteredFAQs.map((faq) => {
              const categoryInfo = getCategoryInfo(faq.category);
              const IconComponent = categoryInfo?.icon || HelpIcon;

              return (
                <Accordion
                  key={faq.id}
                  expanded={expanded === `panel${faq.id}`}
                  onChange={handleAccordionChange(`panel${faq.id}`)}
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      boxShadow: '0 4px 16px rgba(169, 75, 156, 0.15)',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      px: 3,
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: alpha('#A94B9C', 0.02),
                      },
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          backgroundColor: alpha(categoryInfo?.color || '#A94B9C', 0.1),
                          flexShrink: 0,
                        }}
                      >
                        <IconComponent sx={{ fontSize: 22, color: categoryInfo?.color || '#A94B9C' }} />
                      </Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '0.95rem', sm: '1rem' },
                          flex: 1,
                        }}
                      >
                        {faq.question}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3, py: 2, backgroundColor: alpha('#A94B9C', 0.02) }}>
                    <Typography
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.8,
                        fontSize: '0.95rem',
                        ml: 7,
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Box>

        {/* Still Need Help */}
        <Card
          sx={{
            mt: 6,
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha('#A94B9C', 0.05)} 0%, ${alpha(
              '#4B7BA9',
              0.05
            )} 100%)`,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#A94B9C' }}>
            Still need help?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
            Can't find the answer you're looking for? Browse our complete help center or contact our support team.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              onClick={() => navigate('/help')}
              sx={{
                backgroundColor: '#A94B9C',
                px: 4,
                '&:hover': { backgroundColor: '#8E3A7E' },
              }}
            >
              Browse Help Center
            </Button>
            <Button
              variant="outlined"
              href="mailto:support@edifyingteachers.com"
              sx={{
                borderColor: '#A94B9C',
                color: '#A94B9C',
                px: 4,
                '&:hover': {
                  borderColor: '#8E3A7E',
                  backgroundColor: alpha('#A94B9C', 0.1),
                },
              }}
            >
              Contact Support
            </Button>
          </Stack>
        </Card>
      </Container>
    </>
  );
}

export default FAQs;
